import { useCallback, useMemo, useState, type ReactNode, type SyntheticEvent } from "react";
import { Link, useParams } from "react-router-dom";

import {
  isMissing,
  useShareDocument,
  useShareInfo,
  useShareSearch,
  type PublicDocument,
  type ShareInfo,
  type SharedTreeNode,
} from "../../api/shares";
import { Editor } from "../../editor/Editor";
import { useTableOfContents } from "../../editor/hooks";
import { docFromValue } from "../../editor/state";
import { cx } from "../../lib/cx";
import { mapShareLinks, shareLinkIndex } from "./links";

/**
 * The published-share views (`docs/spec/shares.md` §3.4). They render without a session: the
 * link a visitor holds is the only credential involved, and every failure — wrong link,
 * unpublished share, revoked share, document outside the share — lands on the same not-found
 * page, because distinguishing them would confirm that something exists.
 */

function PublicNotFound(): ReactNode {
  return (
    <div className="flex flex-col items-center gap-3 py-24 text-center" role="status">
      <h1 className="text-2xl font-semibold">Not found</h1>
      <p className="text-sm text-muted">This link is no longer available, or it never was.</p>
    </div>
  );
}

function Loading(): ReactNode {
  return (
    <div className="py-24 text-center text-sm text-muted" role="status">
      Loading…
    </div>
  );
}

/* --------------------------------------------------------------- navigation */

function treeHref(shareId: string, node: SharedTreeNode): string | null {
  return node.url.startsWith("/doc/") ? `/s/${encodeURIComponent(shareId)}${node.url}` : null;
}

function TreeNodes({
  shareId,
  nodes,
  activeId,
  depth,
}: {
  shareId: string;
  nodes: SharedTreeNode[];
  activeId: string | undefined;
  depth: number;
}): ReactNode {
  return (
    <ul className={cx("space-y-0.5", depth > 0 && "ml-3 border-l border-line pl-3")}>
      {nodes.map((node) => {
        const href = treeHref(shareId, node);
        return (
          <li key={node.id}>
            {href === null ? (
              <span className="block py-1 text-sm text-muted">{node.title}</span>
            ) : (
              <Link
                to={href}
                aria-current={node.id === activeId ? "page" : undefined}
                className={cx(
                  "block rounded px-2 py-1 text-sm hover:bg-hover",
                  node.id === activeId ? "bg-hover font-medium" : "text-muted",
                )}
              >
                {node.title}
              </Link>
            )}
            {node.children.length > 0 ? (
              <TreeNodes
                shareId={shareId}
                nodes={node.children}
                activeId={activeId}
                depth={depth + 1}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function ShareNavigation({
  shareId,
  tree,
  activeId,
}: {
  shareId: string;
  tree: SharedTreeNode;
  activeId: string | undefined;
}): ReactNode {
  const rootHref = treeHref(shareId, tree);
  return (
    <nav aria-label="Shared documents" className="text-sm">
      {rootHref === null ? (
        <p className="px-2 py-1 font-medium">{tree.title}</p>
      ) : (
        <Link
          to={rootHref}
          aria-current={tree.id === activeId ? "page" : undefined}
          className={cx(
            "block rounded px-2 py-1 font-medium hover:bg-hover",
            tree.id === activeId && "bg-hover",
          )}
        >
          {tree.title}
        </Link>
      )}
      {tree.children.length > 0 ? (
        <TreeNodes shareId={shareId} nodes={tree.children} activeId={activeId} depth={0} />
      ) : null}
    </nav>
  );
}

/* ------------------------------------------------------------------- search */

function ShareSearch({ shareId }: { shareId: string }): ReactNode {
  const [query, setQuery] = useState("");
  const { data, isFetching } = useShareSearch(shareId, query);

  const submit = useCallback((event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get("query");
    setQuery(typeof value === "string" ? value : "");
  }, []);

  return (
    <section className="mb-6">
      <form role="search" onSubmit={submit}>
        <label className="flex flex-col gap-1 text-sm">
          <span className="sr-only">Search this collection</span>
          <input
            type="search"
            name="query"
            placeholder="Search this collection…"
            className="w-full rounded-md border border-line bg-raised px-3 py-2 text-sm"
          />
        </label>
      </form>
      {query.trim() === "" ? null : isFetching ? (
        <p className="mt-2 text-sm text-muted">Searching…</p>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="mt-2 text-sm text-muted">No results.</p>
      ) : (
        <ul className="mt-3 space-y-2" aria-label="Search results">
          {(data ?? []).map((item) => (
            <li key={item.document.id}>
              <Link
                to={`/s/${encodeURIComponent(shareId)}${item.document.url}`}
                className="text-sm font-medium text-accent underline"
              >
                {item.document.title}
              </Link>
              {item.context === undefined ? null : (
                <p className="text-xs text-muted">{item.context.replace(/<\/?b>/g, "")}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- the page */

function formatUpdated(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString().slice(0, 10);
}

function SharedDocument({
  shareId,
  info,
  document: record,
}: {
  shareId: string;
  info: ShareInfo;
  document: PublicDocument;
}): ReactNode {
  const share = info.shares[0];
  const index = useMemo(() => shareLinkIndex(shareId, info.sharedTree), [shareId, info.sharedTree]);

  // Links are mapped before the editor ever sees them, so no internal path is clickable — or
  // even present — in the rendered page (`docs/spec/shares.md` §3.2).
  const doc = useMemo(() => {
    const source = record.data ?? record.text ?? "";
    const json = docFromValue(source).toJSON() as Record<string, unknown>;
    return docFromValue(mapShareLinks(json, index));
  }, [index, record.data, record.text]);

  const toc = useTableOfContents(doc);
  const showToc = (share?.showTOC ?? true) && toc.length > 0;
  const updatedAt = share?.showLastUpdated === true ? record.updatedAt : undefined;
  const tree = info.sharedTree;
  const showTree = tree !== null && (tree.children.length > 0 || share?.collectionId !== null);

  return (
    <div className="flex flex-col gap-8 lg:flex-row">
      {showTree ? (
        <aside className="lg:w-60 lg:shrink-0">
          {share?.collectionId === null ? null : <ShareSearch shareId={shareId} />}
          <ShareNavigation shareId={shareId} tree={tree} activeId={record.id} />
        </aside>
      ) : null}

      <article className="min-w-0 flex-1">
        <header className="mb-6">
          <h1 className="text-3xl font-semibold">{share?.title ?? record.title}</h1>
          {updatedAt === undefined ? null : (
            <p className="mt-1 text-sm text-muted">Last updated {formatUpdated(updatedAt)}</p>
          )}
        </header>

        {showToc ? (
          <nav aria-label="Table of contents" className="mb-6 rounded-md border border-line p-3">
            <ul className="space-y-1 text-sm">
              {toc.map((entry) => (
                <li key={entry.id} style={{ paddingLeft: `${String(entry.depth)}rem` }}>
                  <a href={`#${entry.id}`} className="text-muted hover:text-ink">
                    {entry.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        <Editor value={doc} readOnly fullWidth={record.fullWidth} />
      </article>
    </div>
  );
}

/** `/s/:shareId` — the share's root document, or a collection share's landing page. */
export function ShareScreen(): ReactNode {
  const { shareId = "" } = useParams();
  const info = useShareInfo(shareId);

  if (info.isPending) return <Loading />;
  if (info.isError || info.data === undefined) return <PublicNotFound />;

  const record = info.data.document;
  if (record === null) {
    return <CollectionLanding shareId={shareId} info={info.data} />;
  }
  return <SharedDocument shareId={shareId} info={info.data} document={record} />;
}

/** A collection share has no root document, so its landing page is the tree and the search. */
function CollectionLanding({ shareId, info }: { shareId: string; info: ShareInfo }): ReactNode {
  const share = info.shares[0];
  const tree = info.sharedTree;
  return (
    <div className="flex flex-col gap-8 lg:flex-row">
      <aside className="lg:w-60 lg:shrink-0">
        <ShareSearch shareId={shareId} />
      </aside>
      <section className="min-w-0 flex-1">
        <h1 className="mb-6 text-3xl font-semibold">
          {share?.title ?? info.collection?.name ?? "Shared collection"}
        </h1>
        {info.collection?.description ? (
          <p className="mb-6 text-sm text-muted">{info.collection.description}</p>
        ) : null}
        {tree === null ? (
          <p className="text-sm text-muted">This collection has no published documents yet.</p>
        ) : (
          <ShareNavigation shareId={shareId} tree={tree} activeId={undefined} />
        )}
      </section>
    </div>
  );
}

/** `/s/:shareId/doc/:slug` — one document inside a share that exposes more than one. */
export function ShareDocumentScreen(): ReactNode {
  const { shareId = "", slug = "" } = useParams();
  const info = useShareInfo(shareId);
  const document = useShareDocument(shareId, slug);

  if (info.isPending || document.isPending) return <Loading />;
  if (info.isError || document.isError || info.data === undefined) {
    // A document outside the share is `not_found` like any other miss; it must never redirect
    // to the internal `/doc/...` path, which would leak that the document exists.
    return <PublicNotFound />;
  }
  if (document.data === undefined) return <PublicNotFound />;
  return <SharedDocument shareId={shareId} info={info.data} document={document.data} />;
}

export { PublicNotFound, isMissing };
